import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from 'store';

import { DeviceOutcome, deriveDeviceAvailability, isDeviceAvailable, isFailureOutcome } from '../core';

const selectDevicesState = (state: RootState) => state.devices;

export const selectMicrophoneOutcome = createSelector(selectDevicesState, (state) => state.microphone);
export const selectCameraOutcome = createSelector(selectDevicesState, (state) => state.camera);

/** True while a `getUserMedia` acquisition is in flight — drives the reactive overlay. */
export const selectIsAcquiringDevices = createSelector(selectDevicesState, (state) => state.isAcquiring);

/** Raw per-kind presence (from `enumerateDevices` by `kind`); `null` until first checked. */
export const selectHasMicrophone = createSelector(selectDevicesState, (state) => state.hasMicrophone);
export const selectHasCamera = createSelector(selectDevicesState, (state) => state.hasCamera);

/**
 * Whether BOTH required input devices (microphone AND camera) are present — drives the
 * "connect a device" notice. `null` until first enumerated; `false` = a required device is
 * missing; `true` = both present. Callers should only act on the explicit `false`/`true`.
 */
export const selectHasRequiredDevices = createSelector([selectHasMicrophone, selectHasCamera], (mic, cam) =>
  mic === null || cam === null ? null : mic && cam
);

export const selectIsMicrophoneInFailureState = createSelector(selectMicrophoneOutcome, (outcome) =>
  outcome !== null ? isFailureOutcome(outcome) : false
);
export const selectIsCameraInFailureState = createSelector(selectCameraOutcome, (outcome) =>
  outcome !== null ? isFailureOutcome(outcome) : false
);

export const selectIsMicrophoneAcquired = createSelector(
  selectMicrophoneOutcome,
  (outcome) => outcome === DeviceOutcome.Granted
);
export const selectIsCameraAcquired = createSelector(
  selectCameraOutcome,
  (outcome) => outcome === DeviceOutcome.Granted
);

/** Ambient browser permission per kind — the single source of truth for "do we have access". */
export const selectMicrophonePermission = createSelector(selectDevicesState, (state) => state.microphonePermission);
export const selectCameraPermission = createSelector(selectDevicesState, (state) => state.cameraPermission);

/**
 * Permission is granted for a kind. This is the hand-off point to Agora: once permission is
 * held, Agora creates and owns the track, and any *real-time* device problem (unplugged, busy,
 * muted, not working) is Agora's to surface — NOT the devices module's. So the Agora track gate
 * and in-call track controls key off this, while the setup/join gate uses the richer
 * presence-aware {@link selectMicrophoneAvailability}.
 */
export const selectIsMicrophoneGranted = createSelector(
  selectMicrophonePermission,
  (permission) => permission === 'granted'
);
export const selectIsCameraGranted = createSelector(selectCameraPermission, (permission) => permission === 'granted');

/**
 * The crisp, matrix-collapsing availability per kind — what callers branch on instead of
 * recombining permission + presence themselves. See {@link deriveDeviceAvailability}.
 */
export const selectMicrophoneAvailability = createSelector(
  [selectMicrophonePermission, selectHasMicrophone],
  deriveDeviceAvailability
);
export const selectCameraAvailability = createSelector(
  [selectCameraPermission, selectHasCamera],
  deriveDeviceAvailability
);

/** A device is connected AND granted — the single gate for creating an Agora track. */
export const selectIsMicrophoneAvailable = createSelector(selectMicrophoneAvailability, isDeviceAvailable);
export const selectIsCameraAvailable = createSelector(selectCameraAvailability, isDeviceAvailable);
